import React, { useContext, useEffect, useMemo, useState } from "react";
import "../css/shop.scss";
import { Link } from "react-router-dom";
import { FiHeart, FiSearch, FiSliders, FiX } from "react-icons/fi";
import productData, { siteConfig } from "../Datas/productData";
import { CartContext } from "./CartContext";

function Shop() {
  const { toggleWishlist, isInWishlist, wishlist = [] } = useContext(CartContext);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All pieces");
  const [priceRange, setPriceRange] = useState("all");
  const [sortBy, setSortBy] = useState("featured");
  const [wishlistOnly, setWishlistOnly] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const categories = useMemo(
    () => ["All pieces", ...new Set(productData.map((product) => product.category))],
    []
  );

  useEffect(() => {
    const timer = window.setTimeout(() => setIsLoading(false), 220);
    return () => window.clearTimeout(timer);
  }, []);

  const visibleProducts = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    const filtered = productData.filter((product) => {
      const matchesQuery =
        !normalized ||
        [product.name, product.category, product.material, ...(product.tags || [])]
          .join(" ")
          .toLowerCase()
          .includes(normalized);
      const matchesCategory = category === "All pieces" || product.category === category;
      const matchesWishlist = !wishlistOnly || wishlist.includes(product.id);
      const matchesPrice =
        priceRange === "all" ||
        (priceRange === "under160" && product.price < 160) ||
        (priceRange === "160to175" && product.price >= 160 && product.price <= 175) ||
        (priceRange === "over175" && product.price > 175);
      return matchesQuery && matchesCategory && matchesWishlist && matchesPrice;
    });

    return [...filtered].sort((a, b) => {
      if (sortBy === "price-low") return a.price - b.price;
      if (sortBy === "price-high") return b.price - a.price;
      if (sortBy === "name") return a.name.localeCompare(b.name);
      return Number(b.featured) - Number(a.featured) || a.id - b.id;
    });
  }, [category, priceRange, query, sortBy, wishlist, wishlistOnly]);

  const clearFilters = () => {
    setQuery("");
    setCategory("All pieces");
    setPriceRange("all");
    setSortBy("featured");
    setWishlistOnly(false);
  };

  return (
    <main className="shop-page">
      <header className="shop-hero">
        <p className="eyebrow">Assemblage I</p>
        <h1>The new cravat</h1>
        <p>
          Hand-finished neckwear for those who prefer character to convention. Every shape is
          designed to move between ceremony and the everyday.
        </p>
      </header>

      <section className="shop-controls" aria-label="Collection filters">
        <div className="shop-controls__search">
          <FiSearch aria-hidden="true" />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search the collection"
            aria-label="Search products"
          />
          {query && (
            <button type="button" aria-label="Clear search" onClick={() => setQuery("")}>
              <FiX aria-hidden="true" />
            </button>
          )}
        </div>

        <div className="shop-controls__categories" aria-label="Product categories">
          {categories.map((item) => (
            <button
              type="button"
              className={category === item ? "is-active" : ""}
              key={item}
              onClick={() => setCategory(item)}
            >
              {item}
            </button>
          ))}
        </div>

        <button
          type="button"
          className="shop-controls__filter-toggle"
          aria-expanded={filtersOpen}
          onClick={() => setFiltersOpen((current) => !current)}
        >
          <FiSliders aria-hidden="true" /> Filter & sort
        </button>

        <div className={`shop-controls__secondary ${filtersOpen ? "is-open" : ""}`}>
          <label>
            Price
            <select value={priceRange} onChange={(event) => setPriceRange(event.target.value)}>
              <option value="all">All prices</option>
              <option value="under160">Under €160</option>
              <option value="160to175">€160 – €175</option>
              <option value="over175">Over €175</option>
            </select>
          </label>
          <label>
            Sort by
            <select value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
              <option value="featured">Featured</option>
              <option value="price-low">Price, low to high</option>
              <option value="price-high">Price, high to low</option>
              <option value="name">Name, A–Z</option>
            </select>
          </label>
          <button
            type="button"
            className={`shop-controls__wishlist ${wishlistOnly ? "is-active" : ""}`}
            onClick={() => setWishlistOnly((current) => !current)}
          >
            <FiHeart aria-hidden="true" /> Saved pieces ({wishlist.length})
          </button>
        </div>
      </section>

      <section className="shop-results" aria-live="polite" aria-busy={isLoading}>
        <div className="shop-results__meta">
          <p>{isLoading ? "Preparing collection" : `${visibleProducts.length} pieces`}</p>
          {(query || category !== "All pieces" || priceRange !== "all" || wishlistOnly) && (
            <button type="button" onClick={clearFilters}>Clear all</button>
          )}
        </div>

        {isLoading ? (
          <div className="shop-grid shop-grid--loading" aria-label="Loading products">
            {Array.from({ length: 8 }).map((_, index) => (
              <div className="shop-skeleton" key={index} />
            ))}
          </div>
        ) : visibleProducts.length > 0 ? (
          <div className="shop-grid">
            {visibleProducts.map((product) => (
              <article className="shop-card" key={product.id}>
                <Link className="shop-card__media" to={`/shopDetails/${product.id}`}>
                  <img
                    src={product.image}
                    alt={product.name}
                    loading="lazy"
                    onError={(event) => {
                      event.currentTarget.src = "/main/editorial-arcade.jpg";
                    }}
                  />
                  {product.featured && <span>Atelier selection</span>}
                </Link>
                <button
                  type="button"
                  className={`shop-card__heart ${isInWishlist(product.id) ? "is-active" : ""}`}
                  aria-label={
                    isInWishlist(product.id)
                      ? `Remove ${product.name} from saved pieces`
                      : `Save ${product.name}`
                  }
                  onClick={() => toggleWishlist(product.id)}
                >
                  <FiHeart aria-hidden="true" />
                </button>
                <div className="shop-card__details">
                  <div>
                    <p>{product.category}</p>
                    <h2><Link to={`/shopDetails/${product.id}`}>{product.name}</Link></h2>
                  </div>
                  <span>{siteConfig.currency.symbol}{product.price.toFixed(2)}</span>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="shop-empty">
            <FiSearch aria-hidden="true" />
            <h2>No pieces found</h2>
            <p>Try a different colour, material or price selection.</p>
            <button className="button button--dark" type="button" onClick={clearFilters}>
              Reset collection
            </button>
          </div>
        )}
      </section>
    </main>
  );
}

export default Shop;
